import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import VideoCard from '@/components/base/VideoCard';
import { mockVideos, videoCategories } from '@/mocks/videoData';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const currentSlug = slug || 'all';
  const [activeCategory, setActiveCategory] = useState(currentSlug);

  const filteredVideos = useMemo(() => {
    if (activeCategory === 'all') return mockVideos;
    return mockVideos.filter((v) => v.category === activeCategory);
  }, [activeCategory]);

  const currentCategory = videoCategories.find((c) => c.slug === activeCategory);
  const pageTitle = currentCategory ? `${currentCategory.name} - 青青草视频` : '全部视频 - 青青草视频';

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '分类浏览' },
            ]}
          />

          <div className="mb-8">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">{pageTitle}</h1>
            {currentCategory && (
              <p className="text-sm text-foreground-500">{currentCategory.description}</p>
            )}
          </div>

          {/* Category Tabs */}
          <div className="flex items-center gap-1 overflow-x-auto pb-3 mb-8 flex-nowrap scrollbar-hide">
            {videoCategories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => {
                  setActiveCategory(cat.slug);
                  window.history.replaceState(null, '', `/category/${cat.slug}`);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer flex-shrink-0 ${
                  activeCategory === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <i className={`${cat.icon} text-sm`}></i>
                {cat.name}
              </button>
            ))}
          </div>

          {/* Video Grid */}
          {filteredVideos.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5 pb-12">
              {filteredVideos.map((video) => (
                <VideoCard key={video.id} video={video} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-full">
                <i className="ri-video-line text-foreground-400 text-2xl"></i>
              </div>
              <p className="text-foreground-500">暂无该分类的视频内容</p>
            </div>
          )}
        </div>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: pageTitle,
            description: currentCategory?.description || '青青草视频分类浏览页面',
            isPartOf: {
              '@type': 'WebSite',
              name: '青青草视频',
              url: typeof window !== 'undefined' ? window.location.origin : '',
            },
          }),
        }}
      />
    </>
  );
}